import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { TextField as MaterialTextField, InputAdornment } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import classNames from "classnames";
import styles from "./field-dropdown.module.scss";
import locale from "../../../localization";

const FieldWithDropdown = ({
                             name,
                             value = "",
                             placeholder = "",
                             error,
                             options = [],
                             onChange,
                             onBlur,
                             onFocus,
                             disabled = false,
                             className,
                             upperCase = false,
                           }) => {
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const inputRef = useRef();
  const optionsRef = useRef();

  const filteredOptions = useMemo(() => {
    const q = searchValue?.trim();
    if (!q) {
      return options;
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(q.toLowerCase())
    );
  }, [searchValue, options]);

  const displayValue = useMemo(() => {
    if (isOpen) {
      return searchValue;
    }

    let textToDisplay = "";
    let isShowingSearchValue = false;
    const selectedOption = options.find((opt) => opt.value === value);
    const selectedOptionLabel = selectedOption ? selectedOption.label : "";

    if (searchValue && searchValue !== selectedOptionLabel && searchValue !== selectedLabel) {
      textToDisplay = searchValue;
      isShowingSearchValue = true;
    } else if (selectedLabel) {
      textToDisplay = selectedLabel;
    } else if (selectedOption) {
      textToDisplay = selectedOption.label;
    }

    if (textToDisplay && textToDisplay.length > 42 && !isShowingSearchValue) {
      return textToDisplay.substring(0, 42) + "...";
    }

    return textToDisplay;
  }, [isOpen, searchValue, selectedLabel, value, options]);

  const handleInputChange = useCallback(
    (e) => {
      let inputValue = e.target.value;
      setSearchValue(inputValue);
      if (!inputValue) {
        setSelectedLabel("");
        if (onChange) {
          onChange(name, { value: "", label: "" });
        }
      }
    },
    [onChange, name]
  );

  const handleFocus = useCallback(
    (e) => {
      setIsOpen(true);
      if (onFocus) {
        onFocus(e);
      }
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e) => {
      setTimeout(() => {
        setIsOpen(false);
        // If the text was cleared, ensure display is empty after blur
        if (!searchValue?.trim() && !value && !selectedLabel) {
          setSearchValue("");
        }
        if (onBlur) {
          onBlur(name, { value });
        }
      }, 200);
    },
    [onBlur, name, value, searchValue, selectedLabel]
  );

  const handleOptionSelect = useCallback(
    (option) => {
      setSelectedLabel(option.label);
      setSearchValue(option.label);
      setIsOpen(false);
      if (inputRef.current) {
        const inputElement = inputRef.current.querySelector('input') || inputRef.current.querySelector('textarea');
        if (inputElement && inputElement.blur) {
          inputElement.blur();
        }
      }
      if (onChange) {
        onChange(name, { value: option.value, label: option.label });
      }
    },
    [onChange, name]
  );

  const handleArrowClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const isOptionInactive = useCallback((opt) => {
    const status = (opt?.status || "").toString().toLowerCase();
    return opt?.inactive === true || status === "inactive";
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && 
          inputRef.current && 
          optionsRef.current && 
          !inputRef.current.contains(event.target) && 
          !optionsRef.current.contains(event.target)) {
        setIsOpen(false);
        // If the text was cleared, ensure display is empty
        if (!searchValue?.trim() && !value && !selectedLabel) {
          setSearchValue("");
        }
        if (onBlur) {
          onBlur(name, { value });
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, searchValue, value, selectedLabel, onBlur, name]);

  return (
    <div className={classNames(styles.container, className)}>
      <MaterialTextField
        inputRef={inputRef}
        className={classNames(styles.input, {
          [`${styles.disabled}`]: disabled,
          [`${styles.active}`]: isOpen,
        })}
        error={error}
        value={displayValue}
        variant="outlined"
        placeholder={placeholder}
        disabled={disabled}
        multiline={isOpen}
        maxRows={isOpen ? 1 : 1}
        minRows={1}
        inputProps={{
          maxLength: 150,
          style: isOpen ? {
            overflowY: 'auto',
            resize: 'none'
          } : {}
        }}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon
                className={styles.arrowIcon}
                onClick={handleArrowClick}
              />
            </InputAdornment>
          ),
        }}
      />
      <div
        ref={optionsRef}
        className={classNames(styles.options, {
          [`${styles.open}`]: isOpen,
          [`${styles.empty}`]: !filteredOptions.length,
        })}
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => {
            const inactive = isOptionInactive(option);
            return (
              <div
                key={option.value || index}
                className={classNames(styles.option, {
                  [`${styles.selected}`]: option.value === value,
                })}
                onMouseDown={() => {
                  handleOptionSelect(option);
                }}
              >
                <span className={styles.optionLabel}>{option.label}</span>
                <span className={classNames(styles.inactiveLabel, {
                  [styles.visible]: inactive
                })}>{locale.inactive.toUpperCase()}</span>
              </div>
            );
          })
        ) : (
          <div className={styles.option}>
            <span className={styles.noData}>No options found</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldWithDropdown;